import { Step1ChapterId } from './step1CoverageTopics';

export interface LifeStoryPrompt {
  id: string;
  chapterId: Step1ChapterId;
  coverageTopicIds: string[];
  text: string;
  placeholder?: string;
  /** Optional hint shown below the prompt */
  nudge?: string;
}

export const STEP1_LIFE_STORY_PROMPTS: LifeStoryPrompt[] = [
  {
    id: 'intro_name',
    chapterId: 'ch1',
    coverageTopicIds: ['introduction'],
    text: 'How will you introduce yourself at the start of your First Step?',
    placeholder: 'My name is… and I am a sex addict.',
  },
  {
    id: 'family_overview',
    chapterId: 'ch2',
    coverageTopicIds: ['family_origin'],
    text: 'Describe your early years growing up',
    nudge:
      'What was your family like, how was your relationship with your parents and any siblings, did you have friends, did you move a lot, etc?',
    placeholder: 'I grew up in a family where… (home life, parents, siblings, friends, moves)',
  },
  {
    id: 'family_sex_attitudes',
    chapterId: 'ch2',
    coverageTopicIds: ['family_origin', 'childhood_sexuality'],
    text: 'What did you learn about sex, shame, and secrecy growing up?',
    nudge:
      "When and how were you introduced to sex, what did you feel about sex, did anyone explain the 'birds and the bees' to you, were you abused in any way that impacted your view of sex, what other strong memories about sex do you have from growing up, etc.",
    placeholder: 'Growing up, I learned that sex was… / Messages about sex and secrecy were…',
  },
  {
    id: 'addiction_start',
    chapterId: 'ch3',
    coverageTopicIds: ['addiction_development'],
    text: 'When did you first notice compulsive sexual behavior? What was happening in your life then?',
    placeholder:
      'I first noticed my sexual behavior felt compulsive when… (age, life situation)',
  },
  {
    id: 'progression',
    chapterId: 'ch3',
    coverageTopicIds: ['addiction_development', 'acting_out_patterns'],
    text: 'How did your acting-out escalate over time? What behaviors increased in frequency or intensity?',
    placeholder:
      'Over time, my acting out escalated from… to… (frequency, intensity, new behaviors)',
  },
  {
    id: 'acting_out_examples',
    chapterId: 'ch3',
    coverageTopicIds: ['acting_out_patterns'],
    text: 'What are or were your main acting-out behavior(s)? For each, share the lead-up, what happened in plain language, and how you felt before and after.',
    nudge: 'No graphic sexual detail. Don’t name specific people, places, websites, or apps.',
    placeholder: 'If you have more than one type of acting out, describe each separately.',
  },
  {
    id: 'powerlessness_moment',
    chapterId: 'ch4',
    coverageTopicIds: ['powerlessness'],
    text: 'Describe times you tried to stop or control your behavior and could not — what happened and how did you feel?',
    nudge: 'Powerlessness means you could not stop on your own, even when you wanted to.',
    placeholder:
      "One time I tried to stop or control my behavior by… but I couldn't because…",
  },
  {
    id: 'failed_stops',
    chapterId: 'ch4',
    coverageTopicIds: ['failed_stops', 'powerlessness'],
    text: 'What promises did you make to yourself or others about stopping? What happened when you tried?',
    placeholder:
      'I promised myself (or someone else) I would stop when… Instead, what happened was…',
  },
  {
    id: 'unmanageability_overview',
    chapterId: 'ch5',
    coverageTopicIds: ['unmanageability'],
    text: 'In what areas of life did your addiction create chaos or damage?',
    nudge: 'Think about relationships, work, finances, health, spirituality, self-respect, hope, etc.',
    placeholder:
      'My addiction created chaos in… (e.g. relationships, work, money, health, self-respect, hope)',
  },
  {
    id: 'relationships',
    chapterId: 'ch5',
    coverageTopicIds: ['relationships_impact', 'unmanageability'],
    text: 'How did your addiction affect your closest relationships?',
    placeholder:
      'My closest relationships were affected when I… (lying, distance, broken trust, etc.)',
  },
  {
    id: 'consequences',
    chapterId: 'ch5',
    coverageTopicIds: ['consequences', 'unmanageability'],
    text: 'What concrete consequences did you face — lost relationships, divorces, lost jobs, legal issues, health problems, or other costs?',
    placeholder:
      'Concrete costs I faced included… (e.g. lost relationship, job, legal trouble, health)',
  },
  {
    id: 'recent_episode',
    chapterId: 'ch5',
    coverageTopicIds: ['unmanageability', 'acting_out_patterns'],
    text: 'Describe your last significant acting out episode, how it made you feel, and any consequences',
    placeholder:
      'My last significant acting-out episode: what led up to it, what happened in plain language, how I felt after…',
  },
  {
    id: 'feelings',
    chapterId: 'ch5',
    coverageTopicIds: ['unmanageability'],
    text: 'What feelings did you have about yourself and your life at your worst point?',
    placeholder: 'At my worst, I felt… about myself, my life, and the people around me.',
  },
  {
    id: 'bottom',
    chapterId: 'ch6',
    coverageTopicIds: ['bottom'],
    text: 'What was your bottom — the moment or period when you knew you could not go on this way?',
    placeholder:
      'My bottom was when I realized I could not go on this way — e.g. a crisis, or a moment of despair…',
  },
  {
    id: 'recovery_start',
    chapterId: 'ch6',
    coverageTopicIds: ['recovery_start', 'bottom'],
    text: 'How did you find SAA or begin working recovery?',
    placeholder:
      'I found SAA / began working recovery when… (how you got here, first meeting, sponsor, etc.)',
  },
  {
    id: 'closing',
    chapterId: 'ch7',
    coverageTopicIds: ['closing'],
    text: 'How will you close your First Step?',
    placeholder: 'My name is… and I am a sex addict — and that is my story, so far.',
  },
];

export function promptsForChapter(chapterId: Step1ChapterId): LifeStoryPrompt[] {
  return STEP1_LIFE_STORY_PROMPTS.filter((p) => p.chapterId === chapterId);
}

export function promptById(id: string): LifeStoryPrompt | undefined {
  return STEP1_LIFE_STORY_PROMPTS.find((p) => p.id === id);
}

export function chapterIndex(chapterId: Step1ChapterId): number {
  return STEP1_LIFE_STORY_PROMPTS.findIndex((p) => p.chapterId === chapterId);
}

export function globalPromptIndex(promptId: string): number {
  return STEP1_LIFE_STORY_PROMPTS.findIndex((p) => p.id === promptId);
}
